/**
 * @file This file contains the types used for modelling tables on any v2 page.
 */

import type { smarttable } from 'sap/ui/comp/library';
import type { PopinLayout } from 'sap/m/library';
import type { Toolbar } from './Action';

export enum TableTypeV2Enum {
    ResponsiveTable = 'ResponsiveTable',
    GridTable = 'GridTable',
    AnalyticalTable = 'AnalyticalTable',
    TreeTable = 'TreeTable'
}

export enum LoadDataOnAppLaunchSettingsEnum {
    always = 'always',
    never = 'never',
    ifAnyFilterExist = 'ifAnyFilterExist'
}

export interface DataLoadSettings {
    /**
     * Changes the default behavior for autoloading data.
     */
    loadDataOnAppLaunch: LoadDataOnAppLaunchSettingsEnum;
}

export enum StrategyEnum {
    extension = 'extension'
}

export interface CreateWithFilters {
    /**
     * For the create action to use the filter settings as input, set this property to extension and implement the extension function as mentioned in help.sap.com for SAP S/4HANA, section "Prefilling Fields When Creating a New Entity Using an Extension Point".
     */
    strategy: StrategyEnum;
}

export interface Fields4CreateWithParametersDialog {
    fields: string[];
}

export const enum IgnoredFieldsType {
    GENERICPROPERTY = 'GENERICPROPERTY'
}

export interface MultiEdit {
    /**
     * When multiEdit is enabled, by default the dialog displays editable fields corresponding to the columns that are displayed in the table.
     */
    enabled?: boolean;
    /**
     * Use annotationPath to define a mass edit dialog that points to a FieldGroup annotation with a qualifier.
     */
    annotationPath?: string;
    /**
     * Determines which fields are hidden in key user adaptation and restricts them from being added to the mass edit dialog.
     */
    ignoredFields?: IgnoredFieldsType[];
}

// Start: Interfaces used for the multi-view multi-table scenario (quickVariantSelectionX).

/**
 * @isViewNode false
 */
export interface MultiViewsVariants {
    [key: string]: Table;
}

/**
 * Views
 *
 * @isViewNode true
 */
export interface MultiViewsDefinition {
    /**
     * Use this property to ensure that data are automatically loaded when the page is opened.
     */
    enableAutoBinding?: boolean;
    /**
     * You can show the counts of entries of each view. To do so, add the showCounts option and set it to true.
     */
    showCounts?: boolean;
    /**
     * List of views
     */
    variants: MultiViewsVariants;
}

// End: Interfaces used for the multi-view multi-table scenario (quickVariantSelectionX).

// Start: Interfaces used for the multi-view single-table scenario (quickVariantSelection).

export interface ViewOnTable {
    /**
     * The key property is mandatory. It is used for initializing the corresponding SegmentedButton item.
     */
    key: string;
    /**
     * Use annotationPath to define a specific variant that points to a SelectionVariant or SelectionPresentationVariant annotation.
     */
    annotationPath: string;
}

export interface MultiViewsOnTable {
    /**
     * To show the number of available records, set showCounts to true.
     */
    showCounts?: boolean;
    /**
     * The variants section contains a set of entries that point to a SelectionVariant or SelectionPresentationVariant annotation.
     */
    variants: {
        [key: string]: ViewOnTable;
    };
}

// End: Interfaces used for the multi-view single-table scenario (quickVariantSelection).

/**
 * Table
 *
 * @isViewNode true
 */
export interface Table {
    /**
     * This property can be used to indicate that a table or chart should be based on a different entity set than the main entity set
     */
    entitySet?: string;
    /**
     * Specify a SelectionVariant or SelectionPresentationVariant annotation for the entity set to configure the table or chart
     */
    annotationPath?: string;
    /**
     * To enable navigation for charts, you have to set the property to "true" and maintain an internal navigation target in the manifest.
     */
    showItemNavigationOnChart?: boolean;

    views?: MultiViewsDefinition;

    /**
     * With quickVariantSelection, you can switch to multiple views (single table mode).
     * Each view links to a variant you have added to your annotations.
     */
    quickVariantSelection?: MultiViewsOnTable;

    /**
     * The showTablePersonalisation attribute can be set to false to hide the table personalization button.
     */
    showTablePersonalisation?: boolean;
    /**
     * Specifies the type of export to be used in the SmartTable control.
     */
    exportType?: smarttable.ExportType;
    /**
     * useExportToExcel must be set to true if you want to export data to a spreadsheet application, for example, Microsoft Excel.
     */
    useExportToExcel?: boolean;
    /**
     * Enables the export of data from the SmartTable control to another file, for example, a spreadsheet. If exportType is GW, any $expand parameters are removed when sending the request to generate the file.
     */
    enableExport?: boolean;

    /**
     * Defines the number of items to be requested from the model for each grow. This property can only be used if the growing property is set to true.
     */
    growingThreshold?: number;
    /**
     * Defines the layout in which the table pop-in rows are rendered.
     */
    popinLayout?: PopinLayout;
    /**
     * Set this property to true if you want items to be selectable when they are clicked rather than after the selection control has been set.
     */
    includeItemInSelection?: boolean;

    /**
     * Obsolete property for specifying the type of the table. Use the 'type' property instead.
     */
    tableType?: TableTypeV2Enum;
    /**
     * Specifies the type of the table.
     */
    type?: TableTypeV2Enum;
    /**
     * Sets a condensed table layout for analytical tables, grid tables, and tree tables.
     */
    condensedTableLayout?: boolean;
    /**
     * If you have enabled harmonized variant management, by default, business users cannot set a filter in the table personalization settings.
     * To enable filtering for business users, you must set the enableTableFilterInPageVariant property to true.
     */
    enableTableFilterInPageVariant?: boolean;
    /**
     *  Used to enable inline deletion in a responsive table.
     */
    inlineDelete?: boolean;
    /**
     *  When this setting is enabled, the table switches from single to multi selection.
     */
    multiSelect?: boolean;
    /**
     *  This property can be used to decide whether the 'Select All' checkbox or the 'Clear All' button is shown in the top-left corner of the table.
     */
    selectAll?: boolean;
    /**
     * When new data is loaded from the back-end system, the number of lines is restricted to the specified limit. The default value is 200.
     */
    selectionLimit?: number;
    /**
     * If copy is set to true, the 'Copy to Clipboard' button is shown in the table toolbar.
     * By default, the 'Copy to Clipboard' button is displayed in the table toolbar if the selection mode is configured for the corresponding table.
     */
    copy?: boolean;
    /**
     * Change the default behavior for autoloading of data.
     */
    dataLoadSettings?: DataLoadSettings;
    /**
     * Adds a new create action that uses the filter settings as input.
     */
    createWithFilters?: CreateWithFilters;
    /**
     * Enables the creation of objects that have a maximum of eight data fields through a dialog in the list report page.
     */
    createWithParameterDialog?: Fields4CreateWithParametersDialog;
    /**
     * Allows you to edit multiple objects using a dialog.
     */
    multiEdit?: MultiEdit;
    /**
     * Tool Bar
     *
     * @isViewNode true
     */
    toolbar?: Toolbar;
}
